import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';

interface TypingUser {
  id: string;
  name: string;
}

export function useChatPresence(channelId: string | null) {
  const { user } = useUser();
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Broadcast that this user is typing
  const broadcastTyping = useCallback(() => {
    if (!channelId || !user || !channelRef.current) return;

    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { user_id: user.id, user_name: user.name },
    });
  }, [channelId, user]);

  useEffect(() => {
    if (!channelId || !user) {
      setTypingUsers([]);
      return;
    }

    const presenceChannel = supabase.channel(`presence:${channelId}`);
    channelRef.current = presenceChannel;

    // Track typing timeouts per user
    const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

    presenceChannel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (!payload || payload.user_id === user.id) return;

        const typingUser: TypingUser = {
          id: payload.user_id,
          name: payload.user_name,
        };

        // Add to typing users
        setTypingUsers(prev => {
          if (prev.some(u => u.id === typingUser.id)) return prev;
          return [...prev, typingUser];
        });

        // Clear existing timer for this user
        const existingTimer = typingTimers.get(payload.user_id);
        if (existingTimer) clearTimeout(existingTimer);

        // Remove after 3 seconds of no typing
        const timer = setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u.id !== payload.user_id));
          typingTimers.delete(payload.user_id);
        }, 3000);

        typingTimers.set(payload.user_id, timer);
      })
      .subscribe();

    return () => {
      // Clean up timers
      typingTimers.forEach(timer => clearTimeout(timer));
      typingTimers.clear();
      supabase.removeChannel(presenceChannel);
      channelRef.current = null;
    };
  }, [channelId, user]);

  return { typingUsers, broadcastTyping };
}
